import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import CpvCombobox from "../components/CpvCombobox";
import LegalFooter from "../components/LegalFooter";

const STEPS = ["Capability", "CPV codes", "Certifications", "Scope", "Alerts"];

const CERT_OPTIONS = ["Cyber Essentials", "Cyber Essentials Plus", "ISO 9001", "ISO 27001", "ISO 14001", "JOSCAR", "Hellios"];
const CLEARANCE_OPTIONS = ["BPSS", "SC", "DV"];
const TURNOVER_BANDS = ["<250k", "250k-1m", "1m-5m", "5m-10m", "10m-25m", "25m-50m", ">50m", "unknown"];
const REGIONS = ["North East", "North West", "Yorkshire", "East Midlands", "West Midlands", "East of England", "London", "South East", "South West", "Wales", "Scotland", "Northern Ireland", "Remote/UK-wide"];

export default function Onboarding() {
  const { refresh, activeCompany } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState({
    capability_statement: "",
    cpv_codes: [],
    certifications: [],
    joscar_status: "none",
    hellios_status: "none",
    security_clearances: [],
    turnover_band: "unknown",
    geography: [],
    preferred_buyers: [],
    alert_threshold: 60,
  });
  const [buyerInput, setBuyerInput] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/profile").then(({ data }) => {
      setProfile((p) => ({ ...p, ...data }));
    }).catch(() => {});
  }, [activeCompany?.id]);

  const toggle = (k, v) => {
    setProfile((p) => ({
      ...p,
      [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v],
    }));
  };

  const addBuyer = () => {
    if (buyerInput.trim() && !profile.preferred_buyers.includes(buyerInput.trim())) {
      setProfile((p) => ({ ...p, preferred_buyers: [...p.preferred_buyers, buyerInput.trim()] }));
    }
    setBuyerInput("");
  };

  const save = async () => {
    setError(""); setSaving(true);
    try {
      await api.put("/profile", profile);
      await refresh();
      const next = activeCompany?.subscription_active ? "/app/dashboard" : "/subscribe";
      navigate(next, { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const skip = () => {
    // Persist whatever we have so far in the background so any partial work
    // is kept — but navigate immediately so the user is never stuck.
    api.put("/profile", profile).catch(() => {});
    // Send the user to /subscribe if they haven't paid yet; otherwise straight
    // to the dashboard. AppLayout handles the same routing if we got it wrong.
    const next = activeCompany?.subscription_active ? "/app/dashboard" : "/subscribe";
    navigate(next, { replace: true });
  };

  return (
    <div className="auth-shell" style={{ alignItems: "flex-start", paddingTop: 48 }}>
      <div className="auth-card" style={{ maxWidth: 720 }} data-testid="onboarding-card">
        <div className="flex items-center justify-between mb-4">
          <h1>Company profile</h1>
          <span className="status-pill" data-testid="onboarding-step-pill">Step {step + 1} of {STEPS.length} · {STEPS[step]}</span>
        </div>
        <p className="text-[13px] mb-6" style={{ color: "var(--parapet-text-secondary)" }}>
          Five quick questions to tune your fit scoring across <span className="value-mono">1000s</span> of live opportunities — takes about 90 seconds. You can also skip and complete this later from your profile.
        </p>

        {step === 0 && (
          <div className="space-y-4">
            <div>
              <label className="p-label">Capability statement</label>
              <textarea className="p-input" rows={6} value={profile.capability_statement}
                        onChange={(e) => setProfile({ ...profile, capability_statement: e.target.value })}
                        placeholder="Describe your products, services, technical strengths, and prior MOD/defence experience."
                        data-testid="capability-statement-input" />
              <div className="p-help">Used for opportunity matching context. Be specific.</div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="p-label">CPV codes (Common Procurement Vocabulary)</label>
              <CpvCombobox
                mode="multi"
                value={profile.cpv_codes}
                onChange={(arr) => setProfile((p) => ({ ...p, cpv_codes: arr }))}
                placeholder="Search defence CPV codes (e.g. radar, 35000…)"
                data-testid="cpv-combobox"
              />
              <div className="p-help mt-2">
                Search by keyword or code across the full EU CPV 2008 vocabulary (~9,454 codes).
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label className="p-label">Certifications held</label>
              <div className="flex gap-2 flex-wrap">
                {CERT_OPTIONS.map((c) => (
                  <button type="button" key={c}
                          className={"filter-chip" + (profile.certifications.includes(c) ? " active" : "")}
                          onClick={() => toggle("certifications", c)}
                          data-testid={`cert-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="p-label">Security clearances</label>
              <div className="flex gap-2 flex-wrap">
                {CLEARANCE_OPTIONS.map((c) => (
                  <button type="button" key={c}
                          className={"filter-chip" + (profile.security_clearances.includes(c) ? " active" : "")}
                          onClick={() => toggle("security_clearances", c)}
                          data-testid={`clearance-${c.toLowerCase()}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="p-label">JOSCAR status</label>
                <select className="p-input" value={profile.joscar_status}
                        onChange={(e) => setProfile({ ...profile, joscar_status: e.target.value })}
                        data-testid="joscar-status-select">
                  <option value="none">Not registered</option>
                  <option value="in_progress">In progress</option>
                  <option value="registered">Registered</option>
                </select>
              </div>
              <div>
                <label className="p-label">Hellios status</label>
                <select className="p-input" value={profile.hellios_status}
                        onChange={(e) => setProfile({ ...profile, hellios_status: e.target.value })}
                        data-testid="hellios-status-select">
                  <option value="none">Not registered</option>
                  <option value="in_progress">In progress</option>
                  <option value="registered">Registered</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div>
              <label className="p-label">Turnover band</label>
              <select className="p-input" value={profile.turnover_band}
                      onChange={(e) => setProfile({ ...profile, turnover_band: e.target.value })}
                      data-testid="turnover-select">
                {TURNOVER_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <div className="p-help">Used to assess proportionality of contract value.</div>
            </div>
            <div>
              <label className="p-label">Geography</label>
              <div className="flex gap-2 flex-wrap">
                {REGIONS.map((r) => (
                  <button type="button" key={r}
                          className={"filter-chip" + (profile.geography.includes(r) ? " active" : "")}
                          onClick={() => toggle("geography", r)}
                          data-testid={`region-${r.toLowerCase().replace(/[\s/]+/g, "-")}`}>
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="p-label">Preferred buyers (optional)</label>
              <div className="flex gap-2">
                <input className="p-input" value={buyerInput} onChange={(e) => setBuyerInput(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addBuyer())}
                       placeholder="e.g. Ministry of Defence"
                       data-testid="buyer-input" />
                <button type="button" className="btn-secondary" onClick={addBuyer} data-testid="buyer-add-button">Add</button>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                {profile.preferred_buyers.map((b) => (
                  <span key={b} className="filter-chip active" onClick={() => toggle("preferred_buyers", b)}>
                    {b} <span>×</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <div>
              <label className="p-label">Alert threshold</label>
              <input type="range" min="0" max="100" value={profile.alert_threshold}
                     onChange={(e) => setProfile({ ...profile, alert_threshold: parseInt(e.target.value) })}
                     className="w-full"
                     data-testid="alert-threshold-input" />
              <div className="flex justify-between text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>
                <span>0</span>
                <span className="value-mono">≥ {profile.alert_threshold}</span>
                <span>100</span>
              </div>
              <div className="p-help">Alerts will be raised for opportunities scoring at or above this threshold.</div>
            </div>
          </div>
        )}

        {error && <div className="text-[12px] mt-4">{error}</div>}

        <div className="flex items-center justify-between mt-8">
          <button className="btn-ghost" onClick={prev} disabled={step === 0} data-testid="onboarding-back">Back</button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={skip}
              className="text-[12px] underline"
              style={{ color: "var(--parapet-text-secondary)" }}
              data-testid="onboarding-skip"
            >
              Skip — complete this later
            </button>
            {step < STEPS.length - 1 ? (
              <button className="btn-primary" onClick={next} data-testid="onboarding-next">Continue</button>
            ) : (
              <button className="btn-primary" onClick={save} disabled={saving} data-testid="onboarding-finish">
                {saving ? "Saving…" : "Save profile"}
              </button>
            )}
          </div>
        </div>
      </div>
      <LegalFooter variant="fixed" />
    </div>
  );
}
